// components/modals/HistoryModal.js
import React, { useState } from "react";
import { formatThaiDate } from "../../utils/jobUtils";

const HistoryModal = ({ show, job, history, onClose, onShowErrorDetails }) => {
  const [sortOrder, setSortOrder] = useState("desc"); // desc = newest first, asc = oldest first

  if (!show) return null;

  const sortedHistory = [...history].sort((a, b) => {
    const dateA = new Date(a.created_at);
    const dateB = new Date(b.created_at);
    return sortOrder === "desc" ? dateB - dateA : dateA - dateB;
  });

  const toggleSortOrder = () => {
    setSortOrder(sortOrder === "desc" ? "asc" : "desc");
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal"
        style={{ maxWidth: "900px", width: "95%" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h2 style={{ fontSize: "20px", fontWeight: "600" }}>
            📊 ประวัติงาน - {job.job_name}
          </h2>
          <button className="modal-close" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="modal-body">
          {/* Job Summary */}
          <JobSummaryCard job={job} />

          {/* Timeline Controls */}
          <TimelineControls
            historyCount={history.length}
            sortOrder={sortOrder}
            onToggleSort={toggleSortOrder}
          />

          {/* Timeline */}
          <div style={{ position: "relative" }}>
            {/* Timeline Line */}
            <div
              style={{
                position: "absolute",
                left: "20px",
                top: "0",
                bottom: "0",
                width: "2px",
                background: "#e2e8f0",
                zIndex: "1",
              }}
            />

            {sortedHistory.length > 0 ? (
              sortedHistory.map((entry, index) => (
                <TimelineEntry
                  key={entry.id || index}
                  entry={entry}
                  onShowErrorDetails={onShowErrorDetails}
                  isFirst={index === 0}
                  isLast={index === sortedHistory.length - 1}
                />
              ))
            ) : (
              <EmptyHistoryState />
            )}
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose}>
            ปิด
          </button>
        </div>
      </div>
    </div>
  );
};

// Sub-components
const JobSummaryCard = ({ job }) => (
  <div style={{ marginBottom: "20px" }}>
    <div
      style={{
        background: "#f8fafc",
        border: "1px solid #e2e8f0",
        borderRadius: "8px",
        padding: "16px",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
      }}
    >
      <div>
        <strong>งาน: {job.job_name}</strong>
        <br />
        <small style={{ color: "#64748b" }}>
          ข้อผิดพลาดปัจจุบัน: {job.error_count}/{job.total_records} รายการ
        </small>
      </div>
      <div style={{ textAlign: "right" }}>
        <span
          className={`badge ${
            job.error_count === 0 ? "badge-success" : "badge-error"
          }`}
        >
          {job.error_count === 0
            ? "ไม่มีข้อผิดพลาด"
            : `${job.error_count} ข้อผิดพลาด`}
        </span>
        <br />
        <small style={{ color: "#64748b", marginTop: "4px", display: "block" }}>
          สร้างเมื่อ: {formatThaiDate(job.created_at)}
        </small>
      </div>
    </div>
  </div>
);

const TimelineControls = ({ historyCount, sortOrder, onToggleSort }) => (
  <div
    style={{
      marginBottom: "20px",
      padding: "12px 16px",
      background: "#f1f5f9",
      borderRadius: "6px",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
    }}
  >
    <div>
      <strong>ประวัติการเปลี่ยนแปลง</strong>
      <span style={{ color: "#64748b", marginLeft: "8px" }}>
        ({historyCount} รายการ)
      </span>
    </div>
    <button
      className="btn btn-outline btn-sm"
      onClick={onToggleSort}
      title="เปลี่ยนการเรียงลำดับ"
    >
      {sortOrder === "desc" ? "🔽" : "🔼"}
      {sortOrder === "desc" ? "ใหม่ → เก่า" : "เก่า → ใหม่"}
    </button>
  </div>
);

const TimelineEntry = ({ entry, onShowErrorDetails, isFirst, isLast }) => {
  const getActionConfig = (actionType) => {
    const configs = {
      CREATE: {
        icon: "🎉",
        color: "#10b981",
        bgColor: "#f0fdf4",
        borderColor: "#bbf7d0",
      },
      REUPLOAD: {
        icon: "🔄",
        color: "#f59e0b",
        bgColor: "#fffbeb",
        borderColor: "#fed7aa",
      },
      UPDATE: {
        icon: "✏️",
        color: "#6366f1",
        bgColor: "#eef2ff",
        borderColor: "#c7d2fe",
      },
      DELETE: {
        icon: "🗑️",
        color: "#ef4444",
        bgColor: "#fef2f2",
        borderColor: "#fecaca",
      },
    };

    return configs[actionType] || configs.UPDATE;
  };

  const config = getActionConfig(entry.action_type);
  const hasClickableErrors =
    typeof entry.error_count === "number" && entry.error_count > 0;

  return (
    <div
      style={{
        position: "relative",
        marginBottom: isLast ? "0" : "24px",
        paddingLeft: "50px",
      }}
    >
      {/* Timeline dot */}
      <div
        style={{
          position: "absolute",
          left: "12px",
          top: "8px",
          width: "16px",
          height: "16px",
          borderRadius: "50%",
          background: config.color,
          border: "3px solid white",
          boxShadow: "0 0 0 2px #e2e8f0",
          zIndex: "2",
        }}
      />

      {/* Timeline content */}
      <div
        style={{
          background: "white",
          border: "1px solid #e2e8f0",
          borderRadius: "8px",
          padding: "16px",
          boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "start",
            marginBottom: "8px",
          }}
        >
          <div style={{ flex: 1 }}>
            <h4 style={{ margin: "0 0 4px 0", fontSize: "16px" }}>
              {config.icon} {entry.description}
            </h4>
            <div style={{ display: "flex", gap: "16px", alignItems: "center" }}>
              <small style={{ color: "#64748b" }}>
                โดย: {entry.created_by || "System"}
              </small>
              <span
                style={{
                  fontSize: "11px",
                  padding: "2px 6px",
                  borderRadius: "4px",
                  background: config.bgColor,
                  color: config.color,
                  border: `1px solid ${config.borderColor}`,
                }}
              >
                {entry.action_type}
              </span>
            </div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: "12px", color: "#64748b" }}>
              {formatThaiDate(entry.created_at, {
                year: "numeric",
                month: "short",
                day: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              })}
            </div>
            {typeof entry.error_count === "number" && (
              <ErrorCountBadge
                entry={entry}
                hasClickableErrors={hasClickableErrors}
                onShowErrorDetails={onShowErrorDetails}
              />
            )}
          </div>
        </div>

        {entry.details && (
          <p
            style={{
              margin: "8px 0 0 0",
              fontSize: "14px",
              color: "#64748b",
              lineHeight: "1.5",
            }}
          >
            {entry.details}
          </p>
        )}

        {entry.total_records > 0 && <RecordsSummary entry={entry} />}
      </div>
    </div>
  );
};

const ErrorCountBadge = ({ entry, hasClickableErrors, onShowErrorDetails }) => (
  <div
    style={{
      display: "flex",
      gap: "8px",
      alignItems: "center",
      marginTop: "4px",
    }}
  >
    <span
      className={`badge ${
        entry.error_count === 0 ? "badge-success" : "badge-error"
      }`}
      style={{
        fontSize: "11px",
        cursor: hasClickableErrors ? "pointer" : "default",
        transition: "all 0.2s ease",
      }}
      onClick={() => hasClickableErrors && onShowErrorDetails(entry)}
      onMouseEnter={(e) => {
        if (hasClickableErrors) {
          e.target.style.transform = "scale(1.05)";
        }
      }}
      onMouseLeave={(e) => {
        if (hasClickableErrors) {
          e.target.style.transform = "scale(1)";
        }
      }}
      title={hasClickableErrors ? "คลิกเพื่อดูรายละเอียดข้อผิดพลาด" : ""}
    >
      {entry.error_count === 0
        ? "ไม่มีข้อผิดพลาด"
        : `${entry.error_count} ข้อผิดพลาด`}
    </span>
    {hasClickableErrors && (
      <span
        style={{
          fontSize: "10px",
          color: "#64748b",
          fontStyle: "italic",
        }}
      >
        (คลิกเพื่อดู)
      </span>
    )}
  </div>
);

const RecordsSummary = ({ entry }) => {
  const improvementInfo = getImprovementInfo(entry);

  return (
    <div
      style={{
        marginTop: "12px",
        padding: "8px",
        background: "#f8fafc",
        borderRadius: "4px",
        fontSize: "12px",
      }}
    >
      <div style={{ display: "flex", flexWrap: "wrap", gap: "16px" }}>
        <span>
          📊 ตรวจสอบทั้งหมด:{" "}
          <strong>{entry.total_records.toLocaleString()}</strong> รายการ
        </span>
        {typeof entry.error_count === "number" && (
          <>
            <span>
              ❌ ข้อผิดพลาด:{" "}
              <strong>{entry.error_count.toLocaleString()}</strong> รายการ
            </span>
            <span>
              ✅ ถูกต้อง:{" "}
              <strong>
                {(entry.total_records - entry.error_count).toLocaleString()}
              </strong>{" "}
              รายการ
            </span>
          </>
        )}
      </div>

      {improvementInfo && (
        <div
          style={{
            marginTop: "8px",
            padding: "6px 8px",
            borderRadius: "4px",
            background: improvementInfo.isImprovement ? "#f0fdf4" : "#fef2f2",
            border: `1px solid ${
              improvementInfo.isImprovement ? "#bbf7d0" : "#fecaca"
            }`,
          }}
        >
          <span
            style={{
              color: improvementInfo.isImprovement ? "#059669" : "#dc2626",
              fontWeight: "600",
            }}
          >
            {improvementInfo.icon} {improvementInfo.text}
          </span>
        </div>
      )}
    </div>
  );
};

const getImprovementInfo = (entry) => {
  if (
    typeof entry.previous_error_count === "number" &&
    entry.previous_error_count !== entry.error_count
  ) {
    const difference = Math.abs(entry.previous_error_count - entry.error_count);
    const isImprovement = entry.error_count < entry.previous_error_count;

    return {
      isImprovement,
      icon: isImprovement ? "📈" : "📉",
      text: `${
        isImprovement ? "ปรับปรุง" : "เพิ่มขึ้น"
      }: ${difference} รายการ (จาก ${entry.previous_error_count} → ${
        entry.error_count
      })`,
    };
  }
  return null;
};

const EmptyHistoryState = () => (
  <div style={{ textAlign: "center", padding: "40px" }}>
    <div style={{ fontSize: "48px", marginBottom: "16px" }}>📋</div>
    <h3>ไม่มีประวัติ</h3>
    <p style={{ color: "#64748b" }}>
      ยังไม่มีประวัติการเปลี่ยนแปลงสำหรับงานนี้
    </p>
    <div
      style={{
        marginTop: "20px",
        padding: "16px",
        background: "#f8fafc",
        borderRadius: "8px",
        border: "1px solid #e2e8f0",
      }}
    >
      <small style={{ color: "#64748b" }}>
        💡 ประวัติจะแสดงเมื่อมีการดำเนินการต่างๆ เช่น:
        <br />• สร้างงานใหม่ • อัปโหลด AR04 ใหม่ • แก้ไขข้อมูล • ลบงาน
      </small>
    </div>
  </div>
);

export default HistoryModal;
